import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ArrowRight, LayoutGrid } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import { parseSpanishDate } from "@/lib/dates";
import { CATEGORY_STYLES, DEFAULT_CATEGORY_STYLE } from "@/lib/blogCategories";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import FinalCTA from "@/components/landing/FinalCTA";
import { BLOG_POSTS } from "@/data/blogPosts";

const slugifyCategory = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-");

function BlogCard({ post, delay = 0 }) {
  const meta = CATEGORY_STYLES[post.category] || DEFAULT_CATEGORY_STYLE;
  const Icon = meta.icon;
  return (
    <Link
      to={`/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="reveal card-soft p-6 lg:p-7 flex flex-col group"
      style={{ transitionDelay: `${delay}ms`, backgroundColor: meta.bg, borderColor: meta.border, "--cat-color": meta.accent }}
    >
      <span
        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide"
        style={{ color: meta.accent }}
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
        {post.category}
      </span>
      <h3 className="mt-3 font-display font-extrabold text-xl text-navy-800 leading-snug transition-colors group-hover:text-[var(--cat-color)]">
        {post.title}
      </h3>
      <p className="mt-3 text-slate-600 text-sm leading-relaxed flex-1">{post.excerpt}</p>
      <div className="mt-5 flex items-center justify-between text-xs font-medium" style={{ borderTop: `1px solid ${meta.border}` }}>
        <span className="text-slate-400 pt-3">{post.date}</span>
        <span
          className="inline-flex items-center gap-1 font-bold uppercase tracking-wide group-hover:gap-2 transition-all pt-3"
          style={{ color: meta.accent }}
        >
          Leer más <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

export default function RompiendoElTecho() {
  useReveal();
  const [activeCategory, setActiveCategory] = useState("Todos");

  const sortedPosts = useMemo(
    () => [...BLOG_POSTS].sort((a, b) => parseSpanishDate(b.date) - parseSpanishDate(a.date)),
    []
  );
  const categories = useMemo(
    () => ["Todos", ...Array.from(new Set(sortedPosts.map((p) => p.category)))],
    [sortedPosts]
  );
  const filteredPosts =
    activeCategory === "Todos" ? sortedPosts : sortedPosts.filter((p) => p.category === activeCategory);

  useEffect(() => {
    setPageSEO({
      title: "Rompiendo el Techo | Blog EOS para hacer crecer tu empresa | Carlos Garcín",
      description: "Rompiendo el Techo: el blog de Carlos Garcín con ideas prácticas para dueños de empresa que quieren escalar con foco, alineamiento y disciplina.",
      path: "/rompiendo-el-techo",
      keywords: "blog EOS Chile, Rompiendo el techo, artículos EOS, liderazgo empresarial, accountability empresas",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="Blog — Rompiendo el techo"
          title={
            <>
              Rompiendo el techo: un blog para{" "}
              <span className="text-[#F26522]">hacer crecer tu empresa</span> 🚀
            </>
          }
          subtitle="Dolores y reflexiones de la vida empresarial. Ideas prácticas y sin rodeos para escalar con foco, alineamiento y disciplina basadas en la metodología EOS."
        />

        <section data-testid="blog-articles-section" className="pt-8 sm:pt-10 lg:pt-12 pb-20 sm:pb-24 lg:pb-28 bg-white">
          <div className="container-eos">
            {BLOG_POSTS.length === 0 ? (
              <div className="reveal max-w-xl mx-auto text-center py-8" data-testid="blog-empty-state">
                <span className="eyebrow">Próximamente</span>
                <h2 className="mt-3 font-display font-extrabold text-2xl sm:text-3xl text-navy-800 leading-tight">
                  Estamos preparando el primer artículo
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  Muy pronto vas a encontrar aquí ideas prácticas para romper el techo de tu empresa
                  y escalar con foco, alineamiento y disciplina. Vuelve pronto.
                </p>
              </div>
            ) : (
              <>
                <div className="reveal flex flex-wrap gap-2.5 mb-10" data-testid="blog-category-filters">
                  {categories.map((cat) => {
                    const isAll = cat === "Todos";
                    const meta = isAll ? null : CATEGORY_STYLES[cat] || DEFAULT_CATEGORY_STYLE;
                    const Icon = isAll ? LayoutGrid : meta.icon;
                    const active = activeCategory === cat;
                    const accent = isAll ? "#0A1128" : meta.accent;
                    const border = isAll ? "#CBD5E1" : meta.border;
                    return (
                      <button
                        key={cat}
                        type="button"
                        data-testid={`blog-filter-chip-${slugifyCategory(cat)}`}
                        onClick={() => setActiveCategory(cat)}
                        aria-pressed={active}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide border transition-all hover:-translate-y-0.5"
                        style={
                          active
                            ? { backgroundColor: accent, borderColor: accent, color: "#fff" }
                            : { backgroundColor: "#fff", borderColor: border, color: accent }
                        }
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
                        {cat}
                      </button>
                    );
                  })}
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
                  {filteredPosts.map((post, i) => (
                    <BlogCard key={post.slug} post={post} delay={i * 70} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
